/*
 * The marks a person drew on a Field batch's photographs, and the one body
 * per photograph the outbox sends for them (docs/UI_HUBS_AND_GESTURES_PLAN.md
 * section 5, phase 7). Pure: no React, no store.
 *
 * Two places in Field Mode draw them, and both keep them on the batch, on
 * the phone, under the photo's local id:
 *
 *   - the capture summary's tissue outline (FieldOutline.js):
 *       batch.outline = {photoId, entries, width, height, savedAt}
 *   - the Pro drawer's Annotate (FieldAnnotate.js):
 *       batch.annotations = {[photoId]: {entries, width, height, savedAt}}
 *
 * PUT /photos/{id}/annotations replaces the person's WHOLE set on that
 * photograph (routes/photo_annotations.py), so an outline sent and then the
 * annotations would leave only the second. The outbox's Annotations step
 * therefore sends one body per photograph, both merged:
 *
 *   - per slot (specimen_index and layer: the server takes one entry for
 *     each), the set saved later wins; one with no time counts as the older,
 *     and a tie goes to the Annotate dialog's;
 *   - capped at LIMITS.maxEntries, the later set's entries first;
 *   - checked by validateEntries. A merge the server would refuse (two sets
 *     that are each fine but too detailed together) falls back to the later
 *     set alone, then the earlier alone; when none passes, the photograph's
 *     marks are let go (`problem`) and the record never waits for them.
 *
 * The body is made from what is stored and nothing else, so a retry sends
 * exactly the same body. What the step did for each photograph is kept in
 * `batch.annotationsDone[photoId]` ({sent: true} or {dropped: reason}); new
 * marks on a photograph clear it, so they go too.
 */
import { LIMITS, validateEntries } from './annotationGeometry';

/** Roles that may not annotate photographs (photo_annotations.can_edit:
 *  permissions.NON_WRITER_ROLES). */
export const NON_WRITER_ROLES = Object.freeze(['public', 'read_only', 'company_executive']);

/** Whether this person may draw marks the server would keep. */
export const mayAnnotate = user => (user?.roles || []).some(r => !NON_WRITER_ROLES.includes(r));

/** What the server takes of an entry, nothing a canvas keeps for itself. */
export const cleanEntry = ({ specimen_index: specimenIndex, layer, fertile_type: fertileType, geometry }) => (
  { specimen_index: specimenIndex, layer, fertile_type: fertileType ?? null, geometry });

// image_width / image_height as the server takes them: a whole number of
// pixels up to 20000, else said not at all (rather than refuse the marks).
const side = v => (Number.isInteger(v) && v >= 1 && v <= 20000 ? v : null);
const slot = e => `${e.specimen_index}|${e.layer}`;
const time = s => Date.parse(s?.savedAt || '') || 0;

/** The marks kept for one photograph, newest first: [{kind, entries, width, height, savedAt}]. */
export function marksFor(batch, photoId) {
  const found = [];
  const drawn = batch?.annotations?.[photoId];
  if (drawn?.entries?.length) found.push({ kind: 'annotate', ...drawn });
  const outline = batch?.outline;
  if (outline && outline.photoId === photoId && outline.entries?.length) found.push({ kind: 'outline', ...outline });
  // Stable: a tie keeps the Annotate dialog's first.
  return found.sort((a, b) => time(b) - time(a));
}

/** Several sets of entries as one, the first set's entry winning each slot,
 *  capped at what the server takes. */
export function mergeEntries(sets) {
  const seen = new Set();
  const merged = [];
  for (const entries of sets) {
    for (const entry of entries || []) {
      const key = slot(entry);
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(cleanEntry(entry));
    }
  }
  return merged.slice(0, LIMITS.maxEntries);
}

/**
 * The one PUT body for this photograph, or null when it has no marks:
 * {body: {image_width, image_height, annotations}, problem: null}, or
 * {body: null, problem} when nothing the server would take is left.
 */
export function annotationBody(batch, photoId) {
  const marks = marksFor(batch, photoId);
  if (!marks.length) return null;
  const sized = marks.find(m => side(m.width) && side(m.height)) || {};
  const bodyOf = annotations => ({ image_width: side(sized.width), image_height: side(sized.height), annotations });
  const tries = [marks, ...(marks.length > 1 ? marks.map(m => [m]) : [])];
  let problem = null;
  for (const sets of tries) {
    const annotations = mergeEntries(sets.map(m => m.entries));
    const refused = validateEntries(annotations);
    if (!refused) return { body: bodyOf(annotations), problem: null };
    problem = problem || refused;
  }
  return { body: null, problem };
}

/** The photographs with marks to send, in the batch's photo order (a mark on
 *  a photograph no longer in it last). */
export function annotatedPhotoIds(batch) {
  const ids = new Set(Object.keys(batch?.annotations || {}).filter(id => batch.annotations[id]?.entries?.length));
  if (batch?.outline?.photoId && batch.outline.entries?.length) ids.add(batch.outline.photoId);
  const order = (batch?.photos || []).map(p => p.id);
  return [...ids].sort((a, b) => {
    const at = id => (order.includes(id) ? order.indexOf(id) : order.length);
    return at(a) - at(b);
  });
}

const forget = (done, ids) => {
  if (!done) return done;
  const left = { ...done };
  ids.forEach((id) => { delete left[id]; });
  return Object.keys(left).length ? left : undefined;
};

/**
 * The batch with the Annotate dialog's marks for these photographs:
 * `changes` = {[photoId]: {entries, width, height} | null}; empty entries or
 * null remove that photograph's marks. Each is stamped `savedAt`, and the
 * step's record for it is cleared, so it is sent again.
 */
export function withAnnotations(batch, changes, now = new Date()) {
  const annotations = { ...(batch.annotations || {}) };
  for (const [photoId, change] of Object.entries(changes || {})) {
    const entries = (change?.entries || []).map(cleanEntry);
    if (!entries.length) delete annotations[photoId];
    else {
      annotations[photoId] = { entries, width: side(change.width), height: side(change.height),
        savedAt: now.toISOString() };
    }
  }
  return {
    ...batch,
    annotations: Object.keys(annotations).length ? annotations : undefined,
    annotationsDone: forget(batch.annotationsDone, Object.keys(changes || {})),
  };
}

/** The batch with the summary's tissue outline, stamped `savedAt`; the
 *  photographs it was and is on are sent again. */
export function withOutline(batch, outline, now = new Date()) {
  const ids = [batch.outline?.photoId, outline?.photoId].filter(Boolean);
  return {
    ...batch,
    outline: outline ? { ...outline, entries: (outline.entries || []).map(cleanEntry), savedAt: now.toISOString() }
      : undefined,
    outlineSent: undefined,
    annotationsDone: forget(batch.annotationsDone, ids),
  };
}

/** What is left of the marks when only these photographs stay: a removed
 *  photograph's annotations go with it, and so does an outline drawn on it. */
export function keepAnnotationsFor(batch, photoIds) {
  const kept = new Set(photoIds);
  const annotations = Object.fromEntries(Object.entries(batch?.annotations || {}).filter(([id]) => kept.has(id)));
  const outline = batch?.outline && kept.has(batch.outline.photoId) ? batch.outline : undefined;
  return { annotations: Object.keys(annotations).length ? annotations : undefined, outline };
}
